import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { HomePage } from "@/pages/HomePage";
import { EyTaxiBookingPage } from "@/pages/EyTaxiBookingPage";
import { POSPage } from "@/pages/POSPage";
import { POSStoreBrowser } from "@/pages/POSStoreBrowser";
import { POSStoreFront } from "@/pages/POSStoreFront";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { OrderConfirmationPage } from "@/pages/OrderConfirmationPage";
import { AdminLogin } from "@/pages/AdminLogin";
import { AdminDashboard } from "@/pages/AdminDashboard";
import { AdminOrders } from "@/pages/AdminOrders";
import { AdminSellers } from "@/pages/AdminSellers";
import { AdminCards } from "@/pages/AdminCards";
import { AdminSettings } from "@/pages/AdminSettings";
import { AdminAmazonOrders } from "@/pages/AdminAmazonOrders";
import { AdminMarketplaceOrders } from "@/pages/AdminMarketplaceOrders";
import { AdminMeruRequests } from "@/pages/AdminMeruRequests";
import { AdminCryptoRequests } from "@/pages/AdminCryptoRequests";
import { AdminUsers } from "@/pages/AdminUsers";
import { AdminWithdrawals } from "@/pages/AdminWithdrawals";
import { AdminDrivers } from "@/pages/AdminDrivers";
import { AdminDispatcher } from "@/pages/AdminDispatcher";
import { AdminWalletRequests } from "@/pages/AdminWalletRequests";
// Enterprise B2B imports
import { EnterpriseLogin } from "@/pages/enterprise/EnterpriseLogin";
import { EnterpriseDashboard } from "@/pages/enterprise/EnterpriseDashboard";
import { EnterpriseSellers } from "@/pages/enterprise/EnterpriseSellers";
import { EnterpriseOrders } from "@/pages/enterprise/EnterpriseOrders";
import { EnterpriseUsers } from "@/pages/enterprise/EnterpriseUsers";
import { EnterpriseBudgets } from "@/pages/enterprise/EnterpriseBudgets";
import { EnterpriseReports } from "@/pages/enterprise/EnterpriseReports";
import { EnterpriseSettings } from "@/pages/enterprise/EnterpriseSettings";
import { EnterpriseActivity } from "@/pages/enterprise/EnterpriseActivity";
import { EnterpriseMarketplaceOrders } from "@/pages/enterprise/EnterpriseMarketplaceOrders";
import { NewOrder } from "@/pages/enterprise/NewOrder";
import { OrderConfirmation } from "@/pages/enterprise/OrderConfirmation";
import { LinkAmazonAdmin } from "@/pages/enterprise/LinkAmazonAdmin";
import { LinkAmazonBuyers } from "@/pages/enterprise/LinkAmazonBuyers";
import { GiftcardAdmin } from "@/pages/enterprise/GiftcardAdmin";
import { WhatsAppNotifications } from "@/pages/enterprise/WhatsAppNotifications";
import { EnterpriseDrivers } from "@/pages/enterprise/EnterpriseDrivers";
import { EnterpriseDispatcher } from "@/pages/enterprise/EnterpriseDispatcher";
import { EnterprisePayouts } from "@/pages/enterprise/EnterprisePayouts";
import { EnterpriseWithdrawals } from "@/pages/enterprise/EnterpriseWithdrawals";
// Buyer imports
import { LinkAmazonLogin } from "@/pages/linkamazon/LinkAmazonLogin";
import { LinkAmazonDashboard } from "@/pages/linkamazon/LinkAmazonDashboard";
import { MarketplacePage } from "@/pages/MarketplacePage";
import { ClientTransport } from "@/pages/ClientTransport";
import { SellerDashboard } from "@/pages/SellerDashboard";
import { SellerLogin } from "@/pages/seller/SellerLogin";
import { SellerRegister } from "@/pages/seller/SellerRegister";
import { SellerResetPassword } from "@/pages/seller/SellerResetPassword";
import { DriverDashboard } from "@/pages/driver/DriverDashboard";
import { DriverLogin } from "@/pages/driver/DriverLogin";
import { DriverRegister } from "@/pages/driver/DriverRegister";
import { ResetPasswordPage } from "@/pages/linkamazon/ResetPasswordPage";
import { StorePage } from "@/pages/StorePage";
import { BVGPayWallet } from "@/pages/BVGPayWallet";
import { useEffect } from "react";
import { CartProvider } from "@/context/CartContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function App() {
  useEffect(() => {
    if (!sessionStorage.getItem("bvg_visited")) {
      axios.post(`${API}/track-visitor`, {
        user_agent: navigator.userAgent,
        path: window.location.pathname
      }).catch(err => {});
      sessionStorage.setItem("bvg_visited", "true");
    }
  }, []);

  return (
    <div className="App">
      <CartProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/eytaxi" element={<EyTaxiBookingPage />} />
            <Route path="/transport" element={<ClientTransport />} />
            <Route path="/pos" element={<POSStoreBrowser />} />
            <Route path="/pos/intro" element={<POSPage />} />
            <Route path="/pos/:sellerId" element={<POSStoreFront />} />
            <Route path="/checkout/:cardId" element={<CheckoutPage />} />
            <Route path="/order/:orderId" element={<OrderConfirmationPage />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/cards" element={<AdminCards />} />
            <Route path="/admin/amazon/orders" element={<AdminAmazonOrders />} />
            <Route path="/admin/sellers" element={<AdminSellers />} />
            <Route path="/admin/marketplace/orders" element={<AdminMarketplaceOrders />} />
            <Route path="/admin/meru" element={<AdminMeruRequests />} />
            <Route path="/admin/crypto" element={<AdminCryptoRequests />} />
            <Route path="/admin/wallet-requests" element={<AdminWalletRequests />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/withdrawals" element={<AdminWithdrawals />} />
            <Route path="/admin/drivers" element={<AdminDrivers />} />
            <Route path="/admin/dispatch" element={<AdminDispatcher />} />
            <Route path="/admin/settings" element={<AdminSettings />} />

            {/* Enterprise B2B Routes */}
            <Route path="/enterprise" element={<EnterpriseLogin />} />
            <Route path="/enterprise/login" element={<EnterpriseLogin />} />
            <Route path="/enterprise/dashboard" element={<EnterpriseDashboard />} />
            <Route path="/enterprise/sellers" element={<EnterpriseSellers />} />
            <Route path="/enterprise/orders" element={<EnterpriseOrders />} />
            <Route path="/enterprise/marketplace/orders" element={<EnterpriseMarketplaceOrders />} />
            <Route path="/enterprise/orders/new" element={<NewOrder />} />
            <Route path="/enterprise/orders/confirm" element={<OrderConfirmation />} />
            <Route path="/enterprise/orders/:orderId" element={<EnterpriseOrders />} />
            <Route path="/enterprise/users" element={<EnterpriseUsers />} />
            <Route path="/enterprise/budgets" element={<EnterpriseBudgets />} />
            <Route path="/enterprise/reports" element={<EnterpriseReports />} />
            <Route path="/enterprise/activity" element={<EnterpriseActivity />} />
            <Route path="/enterprise/settings" element={<EnterpriseSettings />} />
            <Route path="/enterprise/linkamazon" element={<LinkAmazonAdmin />} />
            <Route path="/enterprise/linkamazon/buyers" element={<LinkAmazonBuyers />} />
            <Route path="/enterprise/giftcards" element={<GiftcardAdmin />} />
            <Route path="/enterprise/whatsapp" element={<WhatsAppNotifications />} />

            {/* LinkAmazon Routes */}
            <Route path="/linkamazon" element={<LinkAmazonLogin />} />
            <Route path="/linkamazon/login" element={<LinkAmazonLogin />} />
            <Route path="/linkamazon/dashboard" element={<LinkAmazonDashboard />} />
            <Route path="/bvgpay/wallet" element={<BVGPayWallet />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/seller/dashboard" element={<SellerDashboard />} />
            <Route path="/seller/login" element={<SellerLogin />} />
            <Route path="/seller/register" element={<SellerRegister />} />
            <Route path="/seller-reset-password" element={<SellerResetPassword />} />
            <Route path="/driver/dashboard" element={<DriverDashboard />} />
            <Route path="/driver/login" element={<DriverLogin />} />
            <Route path="/driver/register" element={<DriverRegister />} />
            <Route path="/linkamazon/reset-password" element={<ResetPasswordPage />} />
            <Route path="/store/:sellerId" element={<StorePage />} />
            <Route path="/enterprise/drivers" element={<EnterpriseDrivers />} />
            <Route path="/enterprise/drivers" element={<EnterpriseDrivers />} />
            <Route path="/enterprise/dispatch" element={<EnterpriseDispatcher />} />
            <Route path="/enterprise/payouts" element={<EnterprisePayouts />} />
            <Route path="/enterprise/withdrawals" element={<EnterpriseWithdrawals />} />
            
          </Routes>
        </BrowserRouter>
      </CartProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
